/**
 * Hot tub (module `hottub`, Woodlands: Arctic Spas 1,500 L, SpaBoy salt, London hard water).
 * Owner spec: docs/hottub-spec.md. Pure logic shared by the server (report + alerts) and the web
 * dashboard, so the status tile, the "What to do" card and the alerts always agree.
 *
 *   GET    /api/homes/:home/hottub                       → HotTubReport              view
 *   POST   /api/homes/:home/hottub/log                   HotTubLogBody               control
 *   PATCH  /api/homes/:home/hottub/log/:id               HotTubLogPatch              control
 *   DELETE /api/homes/:home/hottub/log/:id                                           control
 *   POST   /api/homes/:home/hottub/readings              HotTubReadingBody           control
 *   PATCH  /api/homes/:home/hottub/readings/:id          HotTubReadingPatch          control
 *   DELETE /api/homes/:home/hottub/readings/:id                                      control
 *   POST   /api/homes/:home/hottub/maintenance/:item     HotTubMaintBody             control
 *   PUT    /api/homes/:home/hottub/settings              Partial<HotTubSettings>     admin
 *   POST   /api/homes/:home/hottub/automations/:entity   {on: boolean}               admin or hottub configure
 *   GET    /api/homes/:home/hottub/audit                 → HotTubAuditRow[]          control
 */
import { z } from 'zod';
// ---------------------------------------------------------------- entities
export const HOTTUB_IDS = {
    ph: 'sensor.ph_level',
    orp: 'sensor.orp',
    /** Water temperature is its `current_temperature` attribute. */
    heater: 'climate.spa_heater',
    /** Pump 1 low speed: the command entity (the controller times it out after ~20 min). */
    pump1Cmd: 'switch.garden_spa_pump_1_low',
    /** Pump 1 as the spa reports it. */
    pump1: 'switch.pump1',
    /** Pumps 2–5: jets, each with a fixed venturi. */
    jets: ['switch.pump2', 'switch.pump3', 'switch.pump4', 'switch.pump5'],
    /** Spa Lights (the tub's own underwater lights; light.hot_tub_led is the UniFi device LED, not this). */
    lights: 'switch.lights',
    /** Sunsynk whole-house grid power, W, + import / − export (docs/energy-spec.md). */
    grid: 'sensor.grid_power',
    /** Hot tub CT circuit, W (heater ~3 kW when it runs). */
    power: 'sensor.08_power_2',
    /** "Spa ready" / override helpers: either on means the tub should be at its target (modes executors use the same pair). */
    ready: 'input_boolean.hot_tub_ready',
    override: 'input_boolean.hot_tub_override',
    /** Phone notifications (HA service notify.mobile_app_nat_iphone: the Eguchi Homes app). */
    notify: 'notify.mobile_app_nat_iphone',
};
/** The ten automations from hot-tub-automations.yaml, matched by alias in the live automation list. */
export const HOTTUB_AUTOMATIONS = [
    { key: 'filtration_start', name: 'Filtration start', alias: 'Hot tub – filtration start', entityId: 'automation.hot_tub_filtration_start', what: 'Keeps pump 1 low running 07:00–09:00 and 19:00–21:00 (restarts it after the spa’s ~20 min timeout) unless the water is above 40.5 °C or it’s switched off by hand' },
    { key: 'filtration_stop', name: 'Filtration stop', alias: 'Hot tub – filtration stop', entityId: 'automation.hot_tub_filtration_stop', what: 'Stops pump 1 at 09:00 and 21:00 (end of each 2 h cycle) unless the jets are in use' },
    { key: 'pump1_watchdog', name: 'Pump 1 watchdog', alias: 'Hot tub – pump 1 watchdog', entityId: 'automation.hot_tub_pump_1_watchdog', what: 'Stops pump 1 after 3.5 h, or if it is still on 30 min after a cycle' },
    { key: 'overheat_guard', name: 'Overheat guard', alias: 'Hot tub – overheat guard', entityId: 'automation.hot_tub_overheat_guard', what: 'Above 40.5 °C for 10 min: stops pump 1 and alerts' },
    { key: 'flapping_alert', name: 'Pump 1 flapping alert', alias: 'Hot tub – pump 1 flapping alert', entityId: 'automation.hot_tub_pump_1_flapping_alert', what: 'Alerts if pump 1 cycles on/off 5 times within 20 s each (something fighting the controller)' },
    { key: 'morning_ph', name: 'Morning pH check', alias: 'Hot tub – morning pH check', entityId: 'automation.hot_tub_morning_ph_check', what: 'At 08:50, if pH > 7.6 and jets off 60 min, sends a dose. Still uses the 29 Sep calibration (14 g per 0.1 pH, target 7.45), not this dashboard\'s 7.5 g / 7.55' },
    { key: 'orp_low', name: 'ORP low', alias: 'Hot tub – ORP low', entityId: 'automation.hot_tub_orp_low', what: 'ORP below 520 mV for 6 h (glitch readings excluded)' },
    { key: 'ph_high_12h', name: 'pH high for 12 h', alias: 'Hot tub – pH high for 12 h', entityId: 'automation.hot_tub_ph_high_for_12_h', what: 'pH above 7.8 for 12 h: scale risk with calcium ~250' },
    { key: 'sensors_offline', name: 'Sensors offline', alias: 'Hot tub – sensors offline', entityId: 'automation.hot_tub_sensors_offline', what: 'pH, ORP or the spa unavailable for 30 min (this dashboard flags it after 10)' },
    { key: 'solar_heat_run', name: 'Solar heat run', alias: 'Hot tub – solar heat run', entityId: 'automation.hot_tub_solar_heat_run', what: 'Runs pump 1 to heat from surplus solar while exporting. Not in Home Assistant yet' },
];
// ---------------------------------------------------------------- settings (calibration constants)
export const HotTubSettings = z.object({
    volumeL: z.number().positive().default(1500),
    phTarget: z.number().min(6).max(9).default(7.55),
    phLow: z.number().min(6).max(9).default(7.5),
    phHigh: z.number().min(6).max(9).default(7.8),
    /** g of pH minus per 0.1 pH (dose = (pH − target) × 10 × this). */
    phMinusPerTenth: z.number().positive().default(7.5),
    /** g of pH minus that removes 1 ppm TA. */
    phMinusPerPpm: z.number().positive().default(3.6),
    sodiumBicarbPerPpm: z.number().positive().default(2.5),
    potassiumBicarbPerPpm: z.number().positive().default(3.0),
    /** Subtracted from a Salifert TA reading (borates read as alkalinity). */
    borateCorrection: z.number().min(0).max(50).default(7),
    taLow: z.number().default(45),
    taHigh: z.number().default(75),
    taTargetLow: z.number().default(50),
    taTargetHigh: z.number().default(60),
    orpTarget: z.number().default(545),
    orpLow: z.number().default(520),
    orpLowHours: z.number().positive().default(6),
    maxTemp: z.number().default(40),
    jetWaitMin: z.number().int().positive().default(60),
    offlineMin: z.number().int().positive().default(10),
    phStuckHours: z.number().positive().default(12),
    drainDays: z.number().int().positive().default(90),
    tdsDrain: z.number().positive().default(5000),
    /** pH-minus usage counts entries from this local date (earlier entries were setup). */
    usageFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).default('2026-09-30'),
    /** Pump 1 minutes a day we expect (2 × 2 h filter cycles). */
    pump1ExpectMin: z.number().int().positive().default(240),
    sequestrantMl: z.number().positive().default(23),
    /** Not heating: water this far (°C) under target … */
    notHeatingDelta: z.number().positive().default(2),
    /** … for this long (min) while wanted (ready / override on) … */
    notHeatingMin: z.number().int().positive().default(45),
    /** … with the circuit drawing less than this (W): the heater isn't running. Mirrors HA automation.hot_tub_not_heating. */
    notHeatingMaxW: z.number().positive().default(2000),
});
export const HOTTUB_DEFAULTS = HotTubSettings.parse({});
// ---------------------------------------------------------------- stored records
export const Chemical = z.enum(['ph_minus', 'bicarb_sodium', 'bicarb_potassium', 'boric_acid', 'salt', 'dichlor', 'sequestrant', 'mps', 'enzyme', 'other']);
export const CHEMICALS = {
    ph_minus: { name: 'pH minus', unit: 'g' },
    bicarb_sodium: { name: 'Sodium bicarbonate', unit: 'g' },
    bicarb_potassium: { name: 'Potassium bicarbonate', unit: 'g' },
    boric_acid: { name: 'Boric acid (borates)', unit: 'g' },
    salt: { name: 'Salt', unit: 'g' },
    dichlor: { name: 'BOOST (dichlor)', unit: 'g' },
    sequestrant: { name: 'Sequestrant', unit: 'ml' },
    mps: { name: 'MPS (non-chlorine shock)', unit: 'g' },
    enzyme: { name: 'Enzyme', unit: 'ml' },
    other: { name: 'Other', unit: 'g' },
};
export const ReadingKind = z.enum(['ta', 'ta_set', 'tds', 'calcium', 'salt', 'borates', 'cya']);
export const READINGS = {
    tds: { name: 'TDS', unit: 'ppm', target: 'drain at 5,000', hi: 5000 },
    calcium: { name: 'Calcium hardness', unit: 'ppm', target: '≤ 250 (keep pH ≤ 7.8)', hi: 250 },
    salt: { name: 'Salt', unit: 'ppm', target: '2,000–2,500', lo: 2000, hi: 2500 },
    borates: { name: 'Borates', unit: 'ppm', target: '30–50', lo: 30, hi: 50 },
    cya: { name: 'CYA (stabiliser)', unit: 'ppm', target: '< 10', hi: 10 },
};
export const MaintItem = z.enum(['refill', 'filter', 'probe', 'salifert', 'sequestrant', 'electrolyser']);
export const MAINT = {
    refill: { name: 'Refill', intervalDays: null, hint: 'Drain and refill' },
    filter: { name: 'Filter swap', intervalDays: 30, hint: 'Monthly' },
    probe: { name: 'Probe check', intervalDays: 30, hint: 'pH pen vs SpaBoy, strip chlorine vs ORP' },
    salifert: { name: 'Salifert TA test', intervalDays: 30, amberDays: 35, hint: 'Monthly; amber after 35 days' },
    sequestrant: { name: 'Sequestrant', intervalDays: 7, hint: 'Weekly, 23 ml' },
    electrolyser: { name: 'Electrolyser on', intervalDays: null, hint: 'Salt cell switched on' },
};
export const HotTubLogBody = z.object({
    chemical: Chemical,
    amount: z.number().positive().max(100_000),
    /** epoch s; default now. */
    at: z.number().int().positive().optional(),
    note: z.string().max(200).optional(),
    countsUsage: z.boolean().nullable().optional(),
});
export const HotTubLogPatch = HotTubLogBody.partial();
export const HotTubReadingBody = z.object({
    kind: ReadingKind,
    value: z.number().min(0).max(100_000),
    /** YYYY-MM-DD (local) or epoch s; default now. */
    date: z.union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/), z.number().int().positive()]).optional(),
    estimated: z.boolean().optional(),
    note: z.string().max(200).optional(),
});
export const HotTubReadingPatch = HotTubReadingBody.partial();
export const HotTubMaintBody = z.object({
    /** YYYY-MM-DD; default today (local). null = never. */
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
});
// ---------------------------------------------------------------- glitch filter
/** SpaBoy pH glitches read 0 or > 9. */
export const validPh = (v) => typeof v === 'number' && Number.isFinite(v) && v > 0 && v <= 9;
/** SpaBoy ORP glitches read below 300 mV. */
export const validOrp = (v) => typeof v === 'number' && Number.isFinite(v) && v >= 300;
export const validTemp = (v) => typeof v === 'number' && Number.isFinite(v) && v > 0 && v < 60;
const BAD = new Set(['unavailable', 'unknown', '']);
export const isBadState = (s) => s === undefined || BAD.has(s);
/** History rows → valid numeric points ([t, v]); unavailable and glitch values dropped. */
export function cleanSeries(rows, valid, read) {
    const out = [];
    for (const r of rows) {
        const v = read ? read(r) : isBadState(r[1]) ? undefined : Number(r[1]);
        if (v === undefined || !Number.isFinite(v) || !valid(v))
            continue;
        out.push([r[0], v]);
    }
    return out;
}
/** On-intervals of a switch from history rows, clipped to [from, to]. Unavailable counts as off. */
export function onIntervals(rows, from, to) {
    const out = [];
    let on = null;
    for (const [t, s] of rows) {
        if (s === 'on') {
            if (on === null)
                on = Math.max(from, t);
        }
        else if (on !== null) {
            if (t > from)
                out.push({ start: on, end: Math.min(t, to) });
            on = null;
        }
    }
    if (on !== null && on < to)
        out.push({ start: on, end: to });
    return out.filter((i) => i.end > i.start && i.end > from && i.start < to).map((i) => ({ start: Math.max(i.start, from), end: Math.min(i.end, to) }));
}
/** Union of intervals; touching or closer than `gap` seconds merge. */
export function mergeIntervals(list, gap = 0) {
    const s = [...list].sort((a, b) => a.start - b.start);
    const out = [];
    for (const i of s) {
        const last = out[out.length - 1];
        if (last && i.start <= last.end + gap)
            last.end = Math.max(last.end, i.end);
        else
            out.push({ ...i });
    }
    return out;
}
export const secondsIn = (list, from, to) => mergeIntervals(list).reduce((a, i) => a + Math.max(0, Math.min(i.end, to) - Math.max(i.start, from)), 0);
/**
 * Soaks: any of pumps 2–5 on; sessions less than `gap` s apart are one soak. Sessions shorter
 * than `minDur` s are dropped: pump 5 blips for ~10 s when pump 1 is started from the panel
 * (seen at both pH-minus doses), which is not a soak and must not start the 60-min wait.
 */
export function jetSessions(byPump, from, to, gap = 600, minDur = 60) {
    const all = [];
    for (const [id, rows] of Object.entries(byPump))
        for (const i of onIntervals(rows, from, to))
            all.push({ ...i, pump: id });
    const merged = mergeIntervals(all.map((a) => ({ start: a.start, end: a.end })), gap).filter((m) => m.end - m.start >= minDur);
    return merged.map((m) => ({ start: m.start, end: m.end, pumps: [...new Set(all.filter((a) => a.start <= m.end && a.end >= m.start).map((a) => a.pump))].sort() }));
}
// ---------------------------------------------------------------- local time
/** Offset (s) of `tz` from UTC at instant t (epoch s). */
function tzOffset(t, tz) {
    const p = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).formatToParts(new Date(t * 1000));
    const g = (k) => Number(p.find((x) => x.type === k)?.value ?? 0);
    const asUtc = Date.UTC(g('year'), g('month') - 1, g('day'), g('hour') % 24, g('minute'), g('second')) / 1000;
    return asUtc - Math.floor(t);
}
/** 'YYYY-MM-DD' in tz. */
export function localDay(t, tz) {
    const d = new Date((t + tzOffset(t, tz)) * 1000);
    return d.toISOString().slice(0, 10);
}
/** Epoch s of local midnight starting `day` (YYYY-MM-DD) in tz. */
export function dayStart(day, tz) {
    const guess = Date.parse(`${day}T00:00:00Z`) / 1000;
    const off1 = tzOffset(guess, tz);
    const t = guess - off1;
    const off2 = tzOffset(t, tz);
    return guess - off2;
}
/** Epoch s of local noon on `day` (where date-only records are stored). */
export const dayNoon = (day, tz) => dayStart(day, tz) + 12 * 3600;
/** Whole days from day a to day b (b − a), DST-safe. */
export function daysBetween(a, b) {
    return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}
export function addDays(day, n) {
    return new Date(Date.parse(`${day}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
}
/** Monday of the week containing `day`. */
export function weekStartDay(day) {
    const dow = new Date(`${day}T00:00:00Z`).getUTCDay(); // 0 Sun
    return addDays(day, -((dow + 6) % 7));
}
// ---------------------------------------------------------------- dose / status
export function doseGrams(ph, s = HOTTUB_DEFAULTS) {
    // Round the pH delta first so float error (7.85 − 7.55 = 0.29999…) can't flip a .5 dose.
    const d = Math.round((ph - s.phTarget) * 1e4) / 1e4;
    return Math.max(0, Math.round(d * s.phMinusPerTenth * 10 + 1e-9));
}
/** Minutes since jets were last on (0 while on), or null if not in the window. */
export function jetMinutesAgo(now, lastJetAt) {
    if (lastJetAt === null || lastJetAt === undefined)
        return null;
    return Math.max(0, Math.floor((now - lastJetAt) / 60));
}
/** Minutes left until a pH reading can be trusted after jet use; 0 when ready. */
export function jetCountdown(now, lastJetAt, waitMin = 60) {
    const ago = jetMinutesAgo(now, lastJetAt);
    if (ago === null)
        return 0;
    return Math.max(0, waitMin - ago);
}
const f1 = (n) => n.toFixed(1);
const f2 = (n) => n.toFixed(2);
/** Status tile (first that applies) plus the "What to do" actions. Spec §1 and §2. */
export function deriveStatus(i, s = HOTTUB_DEFAULTS) {
    const ago = i.jetsOn ? 0 : jetMinutesAgo(i.now, i.lastJetAt);
    const inWait = i.jetsOn || (ago !== null && ago < s.jetWaitMin);
    const waitLeft = i.jetsOn ? s.jetWaitMin : jetCountdown(i.now, i.lastJetAt, s.jetWaitMin);
    const phHigh = i.ph !== undefined && i.ph > s.phHigh;
    const grams = i.ph !== undefined ? doseGrams(i.ph, s) : 0;
    const orpLow = i.orpLowSince !== null && i.orpLowSince !== undefined && i.now - i.orpLowSince >= s.orpLowHours * 3600;
    const hot = i.temp !== undefined && i.temp > s.maxTemp;
    const actions = [];
    if (i.offline.length)
        actions.push({ kind: 'offline', tone: 'alert', title: 'Sensor offline', text: `${i.offline.join(', ')} not reporting for over ${s.offlineMin} min. Check the SpaBoy and spa Wi-Fi before trusting any reading.` });
    if (hot)
        actions.push({ kind: 'too_hot', tone: 'alert', title: 'Too hot', text: `Water is ${f1(i.temp)} °C. Stay out, take the cover off to cool, and keep pump 1 off until it's under ${s.maxTemp} °C.` });
    if (phHigh && inWait) {
        actions.push({ kind: 'wait', tone: 'caution', title: `Re-check in ${waitLeft} min`, text: i.jetsOn ? `Jets are on. pH ${f2(i.ph)} straight after a soak is misleading; wait ${s.jetWaitMin} min after they stop.` : `Jets ran ${ago} min ago, so pH ${f2(i.ph)} isn't settled yet. The dose shows here once ${s.jetWaitMin} min have passed.`, minutes: waitLeft });
    }
    else if (phHigh && grams > 0) {
        actions.push({ kind: 'dose', tone: 'caution', title: `Add ~${grams} g pH minus`, text: `pH ${f2(i.ph)} → target ${s.phTarget}. Dissolve ${grams} g in a jug of tub water and pour it in with pump 1 running. Log it below.`, grams });
    }
    if (orpLow) {
        const h = Math.floor((i.now - i.orpLowSince) / 3600);
        actions.push({ kind: 'orp_low', tone: 'caution', title: 'Chlorine low', text: `ORP has been under ${s.orpLow} mV for ${h} h${i.orp !== undefined ? ` (now ${Math.round(i.orp)} mV)` : ''}. Check the SpaBoy cell is producing and the salt level.` });
    }
    if (i.phStuck)
        actions.push({ kind: 'ph_stuck', tone: 'caution', title: 'pH sensor stuck', text: `pH hasn't changed in ${s.phStuckHours} h. The probe may need cleaning or calibrating: check it against the pH pen.` });
    if (i.ta !== null && i.ta !== undefined) {
        if (i.ta < s.taLow)
            actions.push({ kind: 'ta_low', tone: 'caution', title: 'Alkalinity low: add bicarbonate', text: `Estimate ${Math.round(i.ta)} ppm (below ${s.taLow}). Add bicarbonate and confirm with a Salifert test.` });
        else if (i.ta > s.taHigh)
            actions.push({ kind: 'ta_high', tone: 'caution', title: 'Alkalinity high', text: `Estimate ${Math.round(i.ta)} ppm (above ${s.taHigh}). pH will keep drifting up; confirm with a Salifert test.` });
    }
    const base = { actions };
    if (i.offline.length)
        return { ...base, kind: 'offline', tone: 'alert', title: 'Sensor offline', detail: `${i.offline.join(', ')} not reporting` };
    if (hot)
        return { ...base, kind: 'too_hot', tone: 'alert', title: "Too hot, don't get in", detail: `Water ${f1(i.temp)} °C, limit ${s.maxTemp} °C` };
    if (phHigh && inWait) {
        return {
            ...base, kind: 'wait', tone: 'caution', jetsAgoMin: ago ?? 0, waitMin: waitLeft,
            title: i.jetsOn ? 'Wait: jets are on' : `Wait: jets ran ${ago} min ago`,
            detail: `pH ${f2(i.ph)} · re-check in ${waitLeft} min`,
        };
    }
    if (phHigh)
        return { ...base, kind: 'dose', tone: 'caution', grams, title: `Add ~${grams} g pH minus`, detail: `pH ${f2(i.ph)}, above ${s.phHigh}` };
    if (orpLow)
        return { ...base, kind: 'orp_low', tone: 'caution', title: 'Chlorine low: check SpaBoy', detail: `ORP under ${s.orpLow} mV for ${Math.floor((i.now - i.orpLowSince) / 3600)} h` };
    return {
        ...base, kind: 'ok', tone: 'positive', title: 'All good',
        detail: [i.ph !== undefined ? `pH ${f2(i.ph)}` : null, i.orp !== undefined ? `ORP ${Math.round(i.orp)} mV` : null, i.temp !== undefined ? `${f1(i.temp)} °C` : null].filter(Boolean).join(' · ') || 'No readings yet',
    };
}
/**
 * Not heating (instantaneous): wanted, water ≥ notHeatingDelta under target, circuit under notHeatingMaxW.
 * Unknown readings never fire (the sensor-offline check covers those). The caller times it (notHeatingMin).
 */
export function notHeatingNow(i, s = HOTTUB_DEFAULTS) {
    if (!i.wanted || i.temp === undefined || i.target === undefined || i.powerW === undefined)
        return false;
    return i.target - i.temp >= s.notHeatingDelta && i.powerW < s.notHeatingMaxW;
}
/**
 * Not-heating run: start of the current spell (`prevSince` while it continues, `now` when it starts, null when it stops)
 * and whether it has lasted notHeatingMin (the alert fires).
 */
export function notHeatingCheck(i, now, prevSince, s = HOTTUB_DEFAULTS) {
    if (!notHeatingNow(i, s))
        return { since: null, firing: false };
    const since = prevSince ?? now;
    return { since, firing: now - since >= s.notHeatingMin * 60 };
}
/** Start of the current run of valid ORP below `low` (null if the latest valid reading is fine). */
export function orpLowSince(points, low) {
    let since = null;
    for (const [t, v] of points) {
        if (v < low) {
            if (since === null)
                since = t;
        }
        else
            since = null;
    }
    return since;
}
/** Time the valid value last changed (first point if it never did). */
export function lastChangeAt(points) {
    if (!points.length)
        return null;
    let at = points[0][0];
    for (let k = 1; k < points.length; k++)
        if (points[k][1] !== points[k - 1][1])
            at = points[k][0];
    return at;
}
/**
 * Estimate = last TA reading − borate correction (or a manual set value), then each later
 * pH-minus entry −g/phMinusPerPpm and bicarbonate entry +g/2.5 (sodium) or +g/3.0 (potassium).
 * `steps` also includes earlier bases (each test resets the line) for the step chart.
 */
export function estimateAlkalinity(log, readings, s = HOTTUB_DEFAULTS, asOf = Number.POSITIVE_INFINITY) {
    const evs = [];
    for (const r of readings) {
        if (r.at > asOf || (r.kind !== 'ta' && r.kind !== 'ta_set'))
            continue;
        const v = r.kind === 'ta' ? r.value - s.borateCorrection : r.value;
        evs.push({
            at: r.at, order: 0,
            apply: () => ({ v, step: { delta: 0, kind: r.kind === 'ta' ? 'test' : 'set', label: r.kind === 'ta' ? `Salifert ${r.value} − ${s.borateCorrection}` : `Set to ${r.value}`, refId: r.id } }),
        });
    }
    for (const e of log) {
        if (e.at > asOf)
            continue;
        let d = 0;
        if (e.chemical === 'ph_minus')
            d = -e.amount / s.phMinusPerPpm;
        else if (e.chemical === 'bicarb_sodium')
            d = e.amount / s.sodiumBicarbPerPpm;
        else if (e.chemical === 'bicarb_potassium')
            d = e.amount / s.potassiumBicarbPerPpm;
        else
            continue;
        const kind = e.chemical;
        evs.push({ at: e.at, order: 1, apply: (cur) => (cur === null ? { v: null } : { v: cur + d, step: { delta: d, kind, label: `${CHEMICALS[e.chemical].name} ${e.amount} g`, refId: e.id } }) });
    }
    evs.sort((a, b) => a.at - b.at || a.order - b.order);
    let cur = null;
    let base = null;
    const steps = [];
    for (const ev of evs) {
        const r = ev.apply(cur);
        cur = r.v;
        if (r.step && cur !== null) {
            steps.push({ ...r.step, at: ev.at, value: cur });
            if (r.step.kind === 'test' || r.step.kind === 'set') {
                const rd = readings.find((x) => x.id === r.step.refId);
                base = { kind: r.step.kind, at: ev.at, reading: rd.value, value: cur, id: rd.id };
            }
        }
    }
    return { value: cur, base, steps };
}
// ---------------------------------------------------------------- usage
/** pH minus grams in [fromDay, toDay] (local days), counting only usage entries on/after usageFrom. */
export function phMinusUsed(log, fromDay, tz, s = HOTTUB_DEFAULTS, toT = Number.POSITIVE_INFINITY) {
    const start = Math.max(dayStart(fromDay, tz), dayStart(s.usageFrom, tz));
    return log
        .filter((e) => e.chemical === 'ph_minus' && e.countsUsage !== false && e.at >= start && e.at <= toT)
        .reduce((a, e) => a + e.amount, 0);
}
export function drainInfo(refillDay, tdsReadings, today, s = HOTTUB_DEFAULTS) {
    const sorted = [...tdsReadings].sort((a, b) => a.day.localeCompare(b.day));
    const latest = sorted[sorted.length - 1] ?? null;
    const sinceRefill = refillDay ? sorted.filter((r) => r.day >= refillDay) : sorted;
    const baseline = sinceRefill[0]?.value ?? null;
    if (!refillDay)
        return { refillDay: null, daysSinceRefill: null, dueDay: null, daysLeft: null, tds: latest?.value ?? null, tdsDay: latest?.day ?? null, tdsBaseline: baseline, reason: null, state: 'ok' };
    const due = addDays(refillDay, s.drainDays);
    const left = daysBetween(today, due);
    const byTds = latest !== null && latest.value >= s.tdsDrain;
    const state = byTds || left <= 0 ? 'due' : left <= 14 ? 'soon' : 'ok';
    return {
        refillDay, daysSinceRefill: daysBetween(refillDay, today), dueDay: byTds ? today : due, daysLeft: byTds ? 0 : left,
        tds: latest?.value ?? null, tdsDay: latest?.day ?? null, tdsBaseline: baseline, reason: byTds ? 'tds' : left <= 0 ? 'date' : null, state,
    };
}
export function maintStatus(item, lastDay, today) {
    const m = MAINT[item];
    if (m.intervalDays === null)
        return { item, lastDay, days: lastDay ? daysBetween(lastDay, today) : null, dueDay: null, state: 'none', intervalDays: null };
    if (!lastDay)
        return { item, lastDay: null, days: null, dueDay: today, state: 'late', intervalDays: m.intervalDays };
    const days = daysBetween(lastDay, today);
    const amber = m.amberDays ?? m.intervalDays;
    let state = 'ok';
    if (days >= m.intervalDays * 2)
        state = 'late';
    else if (days >= amber)
        state = 'overdue';
    else if (days >= m.intervalDays - 1)
        state = 'due';
    return { item, lastDay, days, dueDay: addDays(lastDay, m.intervalDays), state, intervalDays: m.intervalDays };
}
//# sourceMappingURL=hottub.js.map